import { useEffect, useMemo, useState, useRef, forwardRef } from "react";
import { useBoolean } from "@fluentui/react-hooks";
import {
  Stack,
  Text,
  Spinner,
  SpinnerSize,
  ContextualMenu,
  IContextualMenuItem,
} from "@fluentui/react";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Description from "@mui/icons-material/Description";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Slideshow from "@mui/icons-material/Slideshow";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import supersub from "remark-supersub";
import rehypeExternalLinks from "rehype-external-links";
import styles from "./Answer.module.css";
import { AskResponse, Citation } from "../../api";
import { parseAnswer } from "./AnswerParser";

declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}

interface Props {
  answer: AskResponse;
  onCitationClicked: (citedDocument: Citation) => void;
  onIntentClicked: (intent: string) => void;
  onSpeak?: any;
  isActive?: boolean;
  index: number;
  isFinalAnswer?: boolean;
}

const MyStackComponent = forwardRef<HTMLDivElement, any>((props, ref) => (
  <div {...props} ref={ref} />
));

export const Answer = ({
  answer,
  onCitationClicked,
  onIntentClicked,
  index,
  isFinalAnswer = true,
}: Props) => {
  const [isRefAccordionOpen, { toggle: toggleIsRefAccordionOpen }] =
    useBoolean(false);
  const filePathTruncationLimit = 50;
  const answerContainerRef = useRef<HTMLDivElement>(null);
  const messageBoxId = "message-" + index;
  const parsedAnswer = useMemo(() => parseAnswer(answer), [answer]);
  const [chevronIsExpanded, setChevronIsExpanded] =
    useState(isRefAccordionOpen);
  const refContainer = useRef<HTMLDivElement>(null);

  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportButtonRef = useRef<HTMLSpanElement>(null);

  const handleChevronClick = () => {
    setChevronIsExpanded(!chevronIsExpanded);
    toggleIsRefAccordionOpen();
  };

  useEffect(() => {
    setChevronIsExpanded(isRefAccordionOpen);
    if (chevronIsExpanded && refContainer.current) {
      refContainer.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chevronIsExpanded, isRefAccordionOpen]);

  const createCitationFilepath = (
    citation: Citation,
    index: number,
    truncate: boolean = false,
  ) => {
    let citationFilename = "";

    if (citation.title && citation.chunk_id != null) {
      if (truncate && citation.title.length > filePathTruncationLimit) {
        const citationLength = citation.title.length;
        citationFilename = `${citation.title.substring(0, 20)}...${citation.title.substring(citationLength - 20)}`;
      } else {
        citationFilename = `${citation.title}`;
      }
    } else if (citation.source_name) {
      citationFilename = citation.source_name
        .replace(/[^a-zA-Z0-9]/g, " ")
        .replace(/\s+$/, "");
    } else {
      citationFilename = `Citation ${index}`;
    }
    return citationFilename;
  };

  const handleExportMessage = async (format: "docx" | "pptx") => {
    setIsExporting(true);
    try {
      const response = await fetch(`/api/export/message/${format}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ content: parsedAnswer.markdownFormatText }),
      });

      if (!response.ok) throw new Error("Export failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        format === "pptx"
          ? `slide_export_${index}.pptx`
          : `message_export_${index}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert(
        `Could not export message to ${format.toUpperCase()}. Please try again.`,
      );
    } finally {
      setIsExporting(false);
    }
  };

  const exportMenuItems: IContextualMenuItem[] = [
    {
      key: "docx",
      text: "Word (.docx)",
      iconProps: {},
      onRenderIcon: () => <Description fontSize="small" />,
      onClick: () => {
        handleExportMessage("docx");
        setShowExportMenu(false);
      },
    },
    {
      key: "pptx",
      text: "PowerPoint (.pptx)",
      iconProps: {},
      onRenderIcon: () => <Slideshow fontSize="small" />,
      onClick: () => {
        handleExportMessage("pptx");
        setShowExportMenu(false);
      },
    },
  ];

  return (
    <>
      <MyStackComponent
        className={
          isFinalAnswer
            ? styles.answerContainer
            : styles.answerContainerStreaming
        }
        id={messageBoxId}
        ref={answerContainerRef}
      >
        <Stack.Item grow>
          <ReactMarkdown
            rehypePlugins={[[rehypeExternalLinks, { target: "_blank", rel: ["noopener", "noreferrer"] }]]}
            remarkPlugins={[remarkGfm, supersub]}
            children={parsedAnswer.markdownFormatText}
            className={styles.answerText}
          />
        </Stack.Item>
        <Stack horizontal className={styles.answerFooter} verticalAlign="start">
          <Stack.Item className={styles.answerDisclaimerContainer}>
            <span
              className={`${styles.answerDisclaimer} ${styles.mobileAnswerDisclaimer}`}
            >
              AI-generated content may be incorrect
            </span>
          </Stack.Item>
          {!!parsedAnswer.citations.length && (
            <Stack.Item aria-label="References">
              <Stack style={{ width: "100%" }}>
                <Stack
                  horizontal
                  horizontalAlign="start"
                  verticalAlign="center"
                  tabIndex={0}
                  onClick={handleChevronClick}
                  role="button"
                  onKeyDown={(e) =>
                    e.key === " " || e.key === "Enter"
                      ? handleChevronClick()
                      : null
                  }
                >
                  <Text className={styles.accordionTitle}>
                    <span>
                      {parsedAnswer.citations.length > 1
                        ? parsedAnswer.citations.length + " references"
                        : "1 reference"}
                    </span>
                  </Text>
                  {chevronIsExpanded ? (
                    <ExpandMore
                      className={styles.accordionIcon}
                      aria-hidden={false}
                    />
                  ) : (
                    <ChevronRight
                      className={styles.accordionIcon}
                      aria-hidden={false}
                    />
                  )}
                </Stack>
              </Stack>
            </Stack.Item>
          )}
        </Stack>
        {chevronIsExpanded && (
          <div
            ref={refContainer}
            style={{
              marginTop: 8,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              gap: "4px",
              maxWidth: "100%",
            }}
          >
            {parsedAnswer.citations.map((citation, idx) => {
              return (
                <span
                  role="button"
                  onKeyDown={(e) =>
                    e.key === " " || e.key === "Enter"
                      ? onCitationClicked(citation)
                      : () => {}
                  }
                  tabIndex={0}
                  title={createCitationFilepath(citation, ++idx)}
                  key={idx}
                  onClick={() => onCitationClicked(citation)}
                  className={styles.citationContainer}
                >
                  <div className={styles.citation} key={idx}>
                    {idx}
                  </div>
                  {createCitationFilepath(citation, idx, true)}
                </span>
              );
            })}
          </div>
        )}

        {isFinalAnswer && (
          <div className={styles.actionButtons}>
            <span
              role="button"
              tabIndex={0}
              onClick={() => onIntentClicked(parsedAnswer.intent)}
              onKeyDown={(e) =>
                e.key === " " || e.key === "Enter"
                  ? onIntentClicked(parsedAnswer.intent)
                  : null
              }
              className={styles.actionButton}
            >
              [?]
            </span>

            {isExporting ? (
              <Spinner
                size={SpinnerSize.xSmall}
                style={{ width: 14, height: 14 }}
              />
            ) : (
              <>
                <span
                  ref={exportButtonRef}
                  role="button"
                  tabIndex={0}
                  title="Export this message"
                  onClick={() => setShowExportMenu(true)}
                  onKeyDown={(e) =>
                    e.key === " " || e.key === "Enter"
                      ? setShowExportMenu(true)
                      : null
                  }
                  className={styles.actionButton}
                >
                  [↓]
                </span>
                <ContextualMenu
                  items={exportMenuItems}
                  hidden={!showExportMenu}
                  target={exportButtonRef}
                  onDismiss={() => setShowExportMenu(false)}
                />
              </>
            )}
          </div>
        )}

        <Stack.Item></Stack.Item>
      </MyStackComponent>
    </>
  );
};
